import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import AdminManageMenu from "../components/AdminManageMenu";
import EditableText from "../components/EditableText";
import TaxonomyManager from "../components/TaxonomyManager";
import { useTaxonomy } from "../hooks/useTaxonomy";
import {
  Star, Plus, Search, MapPin, Shield, AlertTriangle, ThumbsUp,
  ThumbsDown, ChevronDown, CheckCircle, XCircle, HelpCircle
} from "lucide-react";

const RESPONSE_LABELS = {
  // New star-rating values (Phase 58, Sprint 2)
  excellent: { label: "Excellent (5★)", color: "#10B981", icon: CheckCircle, stars: 5 },
  good:      { label: "Good (4★)",      color: "#8DB5A0", icon: CheckCircle, stars: 4 },
  okay:      { label: "Okay (3★)",      color: "#F59E0B", icon: HelpCircle,  stars: 3 },
  poor:      { label: "Poor (2★)",      color: "#EF4444", icon: XCircle,     stars: 2 },
  very_poor: { label: "Very poor (1★)", color: "#DC2626", icon: XCircle,     stars: 1 },
  // Legacy values (still rendered for historical reviews)
  yes:       { label: "Yes",       color: "#10B981", icon: CheckCircle, stars: 5 },
  no:        { label: "No",        color: "#EF4444", icon: XCircle,     stars: 1 },
  sometimes: { label: "Sometimes", color: "#F59E0B", icon: HelpCircle,  stars: 3 },
  always:    { label: "Always",    color: "#10B981", icon: CheckCircle, stars: 5 },
  usually:   { label: "Usually",   color: "#8DB5A0", icon: CheckCircle, stars: 4 },
  rarely:    { label: "Rarely",    color: "#EF4444", icon: XCircle,     stars: 2 },
  partially: { label: "Partially", color: "#F59E0B", icon: HelpCircle,  stars: 3 },
  concerns:  { label: "Concerns",  color: "#EF4444", icon: AlertTriangle, stars: 1 },
};

// Star value → backend string. Single source of truth across all questions
// now that we've replaced Yes/No/Sometimes with a 1-5 star scale.
const STAR_TO_VALUE = { 5: "excellent", 4: "good", 3: "okay", 2: "poor", 1: "very_poor" };
const VALUE_TO_STARS = { excellent: 5, good: 4, okay: 3, poor: 2, very_poor: 1 };

// Static fallback used while the live taxonomy is loading or if the API
// returns no items. Once loaded, the founder-managed
// clinic_rating_criteria taxonomy drives this list.
const FALLBACK_QUESTIONS = [
  { key: "breaks_provided",         label: "Breaks provided as per labour law" },
  { key: "cancellation_pay",        label: "Paid for short-notice cancellations" },
  { key: "payments_on_time",        label: "Payments on time" },
  { key: "sufficient_instruments",  label: "Sufficient instruments provided" },
  { key: "sterilization_protocols", label: "Proper sterilization protocols" },
  { key: "adequate_ppe",            label: "Adequate PPE provided" },
];

/**
 * Split a criterion label like
 *   "Work Environment (workload/ breaks/ scheduling/ ergonomics)"
 * into a main title and a soft subtitle, so labels stay scannable on
 * narrow viewports. Parenthetical items separated by `/` or `,` become
 * a middot-joined subtitle. Labels with no parens render as title-only.
 */
const splitCriterionLabel = (raw) => {
  if (!raw) return { title: "", subtitle: "" };
  const match = raw.match(/^(.+?)\s*\(([^)]+)\)\s*\??\s*$/);
  if (!match) return { title: raw.trim(), subtitle: "" };
  const subtitle = match[2]
    .split(/\s*[/,]\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" · ");
  return { title: match[1].trim(), subtitle };
};

const StarRating = ({ rating, onRate, size = "w-6 h-6" }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map(i => (
      <button key={i} type="button" onClick={() => onRate?.(i)} className="focus:outline-none" data-testid={`star-${i}`}>
        <Star className={`${size} transition-colors ${i <= rating ? 'text-[#8DB5A0] fill-[#8DB5A0]' : 'text-[#E5DFEC]'}`} />
      </button>
    ))}
  </div>
);

const StaticStars = ({ rating, size = "w-4 h-4" }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} className={`${size} ${i <= Math.round(rating) ? 'text-[#8DB5A0] fill-[#8DB5A0]' : 'text-[#E5DFEC]'}`} />
    ))}
  </div>
);

const ResponseBadge = ({ value }) => {
  const info = RESPONSE_LABELS[value];
  if (!info) return <span className="text-xs text-[#64748B]">{value}</span>;
  const Icon = info.icon;
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${info.color}15`, color: info.color }}>
      <Icon className="w-3 h-3" />{info.label}
    </span>
  );
};

const ClinicRatingsPage = () => {
  const { user } = useAuth();
  const { items: criteriaItems, refetch: refetchCriteria } = useTaxonomy("clinic_rating_criteria");
  const [clinics, setClinics] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReview, setShowReview] = useState(false);
  const [searchClinic, setSearchClinic] = useState("");
  const [filterProvince, setFilterProvince] = useState("");
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [agreedDisclaimer, setAgreedDisclaimer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [disclaimer, setDisclaimer] = useState("");

  const [form, setForm] = useState({
    clinic_name: "", clinic_location: "", clinic_province: "AB",
    overall_rating: 0, breaks_provided: "", cancellation_pay: "",
    payments_on_time: "", sufficient_instruments: "",
    sterilization_protocols: "", adequate_ppe: "", pros: "", cons: ""
  });

  // Live-bound list of rating-criteria questions, sourced from the
  // founder-managed `clinic_rating_criteria` taxonomy. Every criterion
  // — canonical or founder-added — is rendered with a 1-5 star control
  // (see "Workplace Assessment" block below). The `type` field that used
  // to gate response styles was removed when we unified everything on
  // stars in Phase 58 Sprint 2.
  const QUESTIONS = useMemo(() => {
    if (!criteriaItems || criteriaItems.length === 0) return FALLBACK_QUESTIONS;
    const mapped = criteriaItems
      .filter((it) => it.is_active !== false && it.key)
      .map((it) => ({ key: it.key, label: it.label || it.key }));
    return mapped.length > 0 ? mapped : FALLBACK_QUESTIONS;
  }, [criteriaItems]);

  useEffect(() => {
    fetchAll();
    axios.get(`${API}/provinces`).then(r => setProvinces(r.data)).catch(() => {});
    axios.get(`${API}/clinics/review-questions`).then(r => setDisclaimer(r.data.disclaimer)).catch(() => {});
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterProvince) params.append("province", filterProvince);
      const [clinicsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/clinics/summary?${params}`),
        axios.get(`${API}/clinics/reviews?${params}`)
      ]);
      setClinics(clinicsRes.data);
      setReviews(reviewsRes.data);
    } catch { toast.error("Failed to load ratings"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [filterProvince]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!form.clinic_name || !form.clinic_location || form.overall_rating === 0) {
      toast.error("Fill in clinic name, location, and overall rating"); return;
    }
    const unanswered = QUESTIONS.filter(q => !form[q.key]);
    if (unanswered.length > 0) {
      toast.error(`Please answer: ${unanswered[0].label}`); return;
    }
    if (!agreedDisclaimer) {
      toast.error("You must agree to the disclaimer"); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/clinics/reviews`, form);
      toast.success(res.data.message || "Review submitted for approval!");
      setShowReview(false);
      resetForm();
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setForm({ clinic_name: "", clinic_location: "", clinic_province: "AB", overall_rating: 0, breaks_provided: "", cancellation_pay: "", payments_on_time: "", sufficient_instruments: "", sterilization_protocols: "", adequate_ppe: "", pros: "", cons: "" });
    setAgreedDisclaimer(false);
  };

  const viewClinicReviews = (clinic) => {
    setSelectedClinic(clinic);
    setSearchClinic(clinic.clinic_name);
  };

  const filteredClinics = clinics.filter(c =>
    !searchClinic || c.clinic_name.toLowerCase().includes(searchClinic.toLowerCase())
  );

  const clinicReviews = reviews.filter(r =>
    selectedClinic && r.clinic_name.toLowerCase() === selectedClinic.clinic_name.toLowerCase()
  );

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="clinic-ratings-page">
        {/* Disclaimer Banner */}
        <div className="bg-[#F0ECF7]/70 border border-[#E5DFEC] rounded-xl p-3 mb-6 flex items-start gap-2" data-testid="clinic-disclaimer">
          <Shield className="w-4 h-4 text-[#6B4E8A] mt-0.5 shrink-0" />
          <p className="text-xs text-[#6B4E8A] leading-relaxed">
            Reviews reflect individual member experiences and opinions. DHDH does not verify the accuracy of clinic ratings or reviews and is not responsible for their content.
          </p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] inline-flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <EditableText blockKey="clinic_ratings.title" fallback="Clinic Ratings" as="span" />
              <TaxonomyManager
                kind="clinic_rating_criteria"
                title="Clinic rating criteria"
                description="Manage the rating-criteria taxonomy used across reviews."
                triggerLabel="Manage rating criteria"
                onChange={refetchCriteria}
              />
            </h1>
            <EditableText
              blockKey="clinic_ratings.subtitle"
              fallback="Anonymous, fact-based workplace reviews by hygienists"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          <Dialog open={showReview} onOpenChange={(open) => { setShowReview(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" data-testid="write-review-btn">
                <Plus className="w-4 h-4 mr-2" /> Write a Review
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Rate a Clinic</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmitReview} className="space-y-5 mt-4">
                <div><Label>Clinic Name *</Label><Input value={form.clinic_name} onChange={e => setForm({...form, clinic_name: e.target.value})} placeholder="Name of the dental clinic" className="mt-2 bg-white border-[#E5DFEC]" data-testid="review-clinic-name" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Location *</Label><Input value={form.clinic_location} onChange={e => setForm({...form, clinic_location: e.target.value})} placeholder="Town / City" className="mt-2 bg-white border-[#E5DFEC]" data-testid="review-clinic-location" /></div>
                  <div><Label>Province</Label>
                    <Select value={form.clinic_province} onValueChange={v => setForm({...form, clinic_province: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                      <SelectContent>{provinces.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div>
                  <Label>Overall Rating *</Label>
                  <div className="mt-2"><StarRating rating={form.overall_rating} onRate={r => setForm({...form, overall_rating: r})} size="w-8 h-8" /></div>
                </div>

                <div className="bg-[#FAF7F2] rounded-xl p-4 space-y-4">
                  <p className="text-sm font-medium text-[#6B4E8A]">Workplace Assessment</p>
                  <p className="text-xs text-[#64748B] -mt-2">Tap a star to rate each item — 1 (very poor) to 5 (excellent).</p>
                  {QUESTIONS.map(q => {
                    const currentStars = VALUE_TO_STARS[form[q.key]] || 0;
                    const { title, subtitle } = splitCriterionLabel(q.label);
                    return (
                      <div key={q.key}>
                        <Label className="text-sm text-[#6B4E8A] block leading-tight">{title}</Label>
                        {subtitle && (
                          <p className="text-[11px] text-[#94A3B8] mt-0.5 leading-snug">{subtitle}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex gap-1" data-testid={`q-stars-${q.key}`}>
                            {[1, 2, 3, 4, 5].map(i => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setForm({ ...form, [q.key]: STAR_TO_VALUE[i] })}
                                className="focus:outline-none transition-transform hover:scale-110"
                                aria-label={`${i} star${i > 1 ? "s" : ""}`}
                                data-testid={`q-${q.key}-star-${i}`}
                              >
                                <Star className={`w-7 h-7 ${i <= currentStars ? "text-[#8DB5A0] fill-[#8DB5A0]" : "text-[#E5DFEC]"}`} />
                              </button>
                            ))}
                          </div>
                          {currentStars > 0 && (
                            <span className="text-xs text-[#64748B]">
                              {(RESPONSE_LABELS[form[q.key]]?.label || "").replace(/ \(\d★\)$/, "")}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div><Label>Pros</Label><Textarea value={form.pros} onChange={e => setForm({...form, pros: e.target.value})} placeholder="What did you like about working here?" className="mt-2 bg-white border-[#E5DFEC]" data-testid="review-pros" /></div>
                <div><Label>Cons</Label><Textarea value={form.cons} onChange={e => setForm({...form, cons: e.target.value})} placeholder="What could be improved?" className="mt-2 bg-white border-[#E5DFEC]" data-testid="review-cons" /></div>

                {/* Disclaimer */}
                <div className="bg-[#8DB5A0]/10 border border-[#8DB5A0]/30 rounded-xl p-4">
                  <p className="text-xs text-[#64748B] whitespace-pre-line mb-3">{disclaimer || "By submitting, you agree your review is truthful, factual, and does not contain defamatory content."}</p>
                  <div className="flex items-start space-x-2">
                    <Checkbox id="agree" checked={agreedDisclaimer} onCheckedChange={c => setAgreedDisclaimer(c)} data-testid="agree-disclaimer" />
                    <Label htmlFor="agree" className="text-sm text-[#6B4E8A] cursor-pointer leading-tight">I agree to the terms above and confirm my review is based on firsthand experience</Label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => { setShowReview(false); resetForm(); }} className="rounded-full">Cancel</Button>
                  <Button type="submit" className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" disabled={submitting || !agreedDisclaimer} data-testid="submit-review-btn">{submitting ? "Submitting..." : "Submit Review"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input value={searchClinic} onChange={e => { setSearchClinic(e.target.value); if (!e.target.value) setSelectedClinic(null); }} placeholder="Search clinics..." className="pl-10 bg-white border-[#E5DFEC]" data-testid="clinic-search" />
          </div>
          <Select value={filterProvince || "all"} onValueChange={v => setFilterProvince(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 bg-white border-[#E5DFEC]"><SelectValue placeholder="Province" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Provinces</SelectItem>{provinces.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-28 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
        ) : (
          <>
            {/* Clinic Summary Cards */}
            {!selectedClinic && filteredClinics.length > 0 && (
              <div className="space-y-4 mb-8">
                {filteredClinics.map((clinic, idx) => (
                  <motion.div
                    key={`${clinic.clinic_name}-${clinic.clinic_location}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="bg-white border border-[#E5DFEC] rounded-2xl p-5 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => viewClinicReviews(clinic)}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{clinic.clinic_name}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1 text-sm text-[#64748B]"><MapPin className="w-3.5 h-3.5" />{clinic.clinic_location}</span>
                          <span className="text-xs bg-[#F0ECF7] px-2 py-0.5 rounded-full text-[#6B4E8A]">{clinic.clinic_province}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-medium text-[#6B4E8A]">{clinic.avg_rating}</span>
                            <StaticStars rating={clinic.avg_rating} />
                          </div>
                          <p className="text-xs text-[#64748B]">{clinic.review_count} review{clinic.review_count !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    </div>
                    {/* Quick stats */}
                    <div className="flex flex-wrap gap-3 mt-3">
                      {clinic.breaks_yes > 0 && <span className="text-xs px-2 py-1 rounded-full bg-[#10B981]/10 text-[#10B981]">Breaks: {clinic.breaks_yes} yes</span>}
                      {clinic.breaks_no > 0 && <span className="text-xs px-2 py-1 rounded-full bg-[#EF4444]/10 text-[#EF4444]">Breaks: {clinic.breaks_no} no</span>}
                      {clinic.pay_on_time_always > 0 && <span className="text-xs px-2 py-1 rounded-full bg-[#10B981]/10 text-[#10B981]">Pay always on time: {clinic.pay_on_time_always}</span>}
                      {clinic.sterilization_concerns > 0 && <span className="text-xs px-2 py-1 rounded-full bg-[#EF4444]/10 text-[#EF4444]">Sterilization concerns: {clinic.sterilization_concerns}</span>}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Selected Clinic Detail + Reviews */}
            {selectedClinic && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <button onClick={() => { setSelectedClinic(null); setSearchClinic(""); }} className="text-sm text-[#8DB5A0] hover:text-[#D0694E]">All Clinics</button>
                  <span className="text-[#E5DFEC]">/</span>
                  <span className="text-sm text-[#6B4E8A] font-medium">{selectedClinic.clinic_name}</span>
                </div>

                <div className="bg-white border border-[#E5DFEC] rounded-2xl p-6 mb-6">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-4xl font-medium text-[#6B4E8A]">{selectedClinic.avg_rating}</span>
                    <div>
                      <StaticStars rating={selectedClinic.avg_rating} size="w-5 h-5" />
                      <p className="text-sm text-[#64748B] mt-1">{selectedClinic.review_count} review{selectedClinic.review_count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <p className="text-sm text-[#64748B] flex items-center gap-1"><MapPin className="w-4 h-4" />{selectedClinic.clinic_location}, {selectedClinic.clinic_province}</p>
                </div>

                {clinicReviews.length > 0 ? clinicReviews.map((review, idx) => (
                  <motion.div key={review.review_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} className="relative bg-white border border-[#E5DFEC] rounded-2xl p-5">
                    <div className="absolute top-2 right-2 z-10">
                      <AdminManageMenu
                        resourceType="clinic-review"
                        resourceId={review.review_id}
                        resourceLabel={`review for ${selectedClinic?.clinic_name}`}
                        onDeleted={fetchAll}
                      />
                    </div>
                    <div className="flex items-center justify-between mb-3 pr-8">
                      <div className="flex items-center gap-3">
                        <StaticStars rating={review.overall_rating} />
                        {review.anon_label && <span className="text-xs font-medium text-[#6B4E8A]" data-testid={`review-label-${review.review_id}`}>{review.anon_label}</span>}
                      </div>
                      <span className="text-xs text-[#64748B]">{new Date(review.created_at).toLocaleDateString("en-CA")}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                      {QUESTIONS.map(q => (
                        <div key={q.key} className="flex items-center justify-between text-xs">
                          <span className="text-[#64748B] truncate mr-2" title={q.label}>{splitCriterionLabel(q.label).title}</span>
                          <ResponseBadge value={review[q.key]} />
                        </div>
                      ))}
                    </div>
                    {review.pros && (
                      <div className="flex items-start gap-2 mb-2">
                        <ThumbsUp className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />
                        <p className="text-sm text-[#6B4E8A]">{review.pros}</p>
                      </div>
                    )}
                    {review.cons && (
                      <div className="flex items-start gap-2">
                        <ThumbsDown className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" />
                        <p className="text-sm text-[#6B4E8A]">{review.cons}</p>
                      </div>
                    )}
                  </motion.div>
                )) : (
                  <div className="text-center py-8 bg-white border border-[#E5DFEC] rounded-2xl">
                    <p className="text-[#64748B]">No individual reviews visible yet (pending approval)</p>
                  </div>
                )}
              </div>
            )}

            {/* No clinics at all */}
            {!selectedClinic && filteredClinics.length === 0 && (
              <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
                <Star className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No clinic ratings yet</h3>
                <p className="text-[#64748B] mb-4">Be the first to rate a workplace</p>
                <Button onClick={() => setShowReview(true)} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full">
                  <Plus className="w-4 h-4 mr-2" /> Write a Review
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ClinicRatingsPage;
